import Fetch from 'utils/fetch';

export const mapboxApiKey =
  'pk.eyJ1IjoicG9ydGVyYnVkZHkiLCJhIjoiY2twNnNjM3I0MGVyaTJub2Y3Mmk1dWJuMiJ9.-zWILBeIKyKRx_tC_U6gVA';

function splitCoordinates(coordinates: [number, number][]) {
  const segmentSize = 25;
  const segments = [];
  let currentSegment = [];
  for (let i = 0; i < coordinates.length; i++) {
    currentSegment.push(coordinates[i]);
    if (i === coordinates.length - 1) {
      // Last element was pushed on, save the current segment as it will never reach max size for the segement
      segments.push(currentSegment);
    } else if (currentSegment.length === segmentSize - 1) {
      // Max size of segment reached. Add current plus the next entry (first of the next segment) to segment, and start a new one.
      currentSegment.push(coordinates[i + 1]);
      segments.push(currentSegment);
      currentSegment = [coordinates[i + 1]];
    }
  }
  return segments;
}

export type DirectionsData = {
  distance: number;
  duration: number;
  waypoints: Waypoint[];
  routeCoordinates: [number, number][];
};

export async function fetchDirections(
  profile: RoutingProfile,
  coordinates: [number, number][]
): Promise<DirectionsData | null> {
  const allData: DirectionResponse[] = [];
  const splittedCoordinates = splitCoordinates(coordinates);
  for (let i = 0; i < splitCoordinates(coordinates).length; i += 1) {
    const coordinatesAsQueryString = splittedCoordinates[i]
      .map(cord => `${cord[0]},${cord[1]}`)
      .join(';');

    console.log(`coordinatesAsQueryString`, coordinatesAsQueryString);
    const response = await Fetch.get({
      url: `https://api.mapbox.com/directions/v5/mapbox/${profile}/${encodeURIComponent(
        coordinatesAsQueryString
      )}?alternatives=true&geometries=geojson&language=en&overview=full&steps=true&access_token=${mapboxApiKey}`,
    });

    if (!response.ok) {
      return null;
    }

    const data: DirectionResponse = await response.json();

    /* Only add the data that has the routes */
    if (data.routes.length > 0) {
      allData.push(data);
    }
  }

  return allData.reduce(
    (acc, cur) => {
      let distance = acc.distance;
      let duration = acc.duration;
      let waypoints = acc.waypoints;
      let routeCoordinates = acc.routeCoordinates;

      if (cur.routes?.length > 0) {
        distance += cur.routes[0].distance;
        duration += cur.routes[0].duration;
        routeCoordinates = routeCoordinates.concat(
          cur.routes[0].geometry.coordinates
        );
      }

      if (cur.waypoints?.length > 0) {
        waypoints = waypoints.concat(cur.waypoints);
      }

      return {
        distance,
        duration,
        waypoints,
        routeCoordinates,
      };
    },
    {
      distance: 0,
      duration: 0,
      waypoints: [],
      routeCoordinates: [],
    }
  );
}

export type RoutingProfile =
  | 'driving'
  | 'driving-traffic'
  | 'walking'
  | 'cycling';

export type Waypoint = {
  distance: number;
  name: string;
  location: [number, number][];
};

type DirectionResponse = {
  code: string;
  waypoints: Waypoint[];
  routes: Route[];
};

type Route = {
  distance: number;
  duration: number;
  geometry: Geometry;
  legs: Leg[];
  weight: number;
  weight_name: string;
  duration_typical?: number;
  weight_typical?: number;
};

type Geometry = {
  coordinates: [number, number][];
  type: 'LineString';
};

type Leg = {
  via_waypoints: unknown[];
  admins: string[];
  weight: number;
  duration: number;
  steps: Step[];
  distance: number;
  summary: string;
};

type Step = {
  distance: number;
  duration: number;
  maneuver: {
    type: string;
    modifier: string;
    instruction: string;
  };
  name: string;
};
